import Swal from 'sweetalert2';
import { escapeHtml } from '@shared/escape-html.utils';
import { evidenceLabelOf, fileNameOf } from './evaluation-evidence.helpers';

/** Abre el diálogo para adjuntar evidencia y lee el resultado. */
export function requestEvidenceInput(target: any, label: string, kind: string): Promise<any> {
  return Swal.fire(evidencePromptConfig(target, label, kind));
}

/** Arma la configuración del diálogo de evidencia. */
export function evidencePromptConfig(target: any, label: string, kind: string): any {
  return {
    title: 'Adjuntar evidencia',
    html: buildEvidenceModalHtml(kind, label, target.comments || '', target.file?.originalName || ''),
    showCancelButton: true,
    confirmButtonText: 'Guardar',
    cancelButtonText: 'Cancelar',
    focusConfirm: false,
    preConfirm: readEvidenceInput,
  };
}

/** Lee el archivo y texto capturados en el diálogo. */
export function readEvidenceInput(): any {
  return { file: selectedPromptFile(), text: promptText() };
}

/** Obtiene el archivo elegido en el diálogo. */
function selectedPromptFile(): File | null {
  const input = document.getElementById('ev-file') as HTMLInputElement;
  return input?.files?.[0] || null;
}

/** Obtiene el texto capturado en el diálogo. */
function promptText(): string | null {
  const input = document.getElementById('ev-text') as HTMLTextAreaElement;
  if (!input) return null;
  return input.value;
}

/** Construye el HTML del modal para adjuntar evidencia. */
export function buildEvidenceModalHtml(kind: string, label: string, currentText: string, currentFile: string): string {
  return modalHead(kind, label) + fileSection(kind, currentFile) + textSection(kind, currentText) + '</div>';
}

/** Construye el encabezado del modal de evidencia. */
function modalHead(kind: string, label: string): string {
  return modalHeadHtml(escapeHtml(label), evidenceLabelOf(kind));
}

/** Une el encabezado con etiqueta escapada. */
function modalHeadHtml(label: string, kindLabel: string): string {
  return '<div class="text-start"><p class="small text-muted mb-2">Evidencia requerida pregunta '
    + label + ': <b>' + kindLabel + '</b></p>';
}

/** Construye la sección de archivo si el tipo la requiere. */
function fileSection(kind: string, currentFile: string): string {
  if (kind === 'file' || kind === 'file_or_text') return fileInputHtml(currentFile);
  return '';
}

/** Construye el campo de carga de archivo del modal. */
function fileInputHtml(currentFile: string): string {
  return '<div class="mb-2"><label class="form-label small">Archivo ' + currentSuffix(currentFile)
    + '</label><input id="ev-file" type="file" class="form-control"></div>';
}

/** Muestra el nombre del archivo actual si existe. */
function currentSuffix(currentFile: string): string {
  if (!currentFile) return '';
  return '(actual: ' + escapeHtml(currentFile) + ')';
}

/** Construye la sección de texto si el tipo la requiere. */
function textSection(kind: string, currentText: string): string {
  if (kind === 'text' || kind === 'file_or_text') return textInputHtml(currentText);
  return '';
}

/** Construye el campo de observaciones del modal. */
function textInputHtml(currentText: string): string {
  return '<div class="mb-2"><label class="form-label small">Observaciones</label>'
    + '<textarea id="ev-text" rows="5" class="form-control" placeholder="Ingrese observaciones">'
    + escapeHtml(currentText) + '</textarea></div>';
}

/** Muestra la evidencia de texto cuando no hay archivo. */
export function showTextEvidence(text: string): void {
  Swal.fire({ title: 'Evidencia', html: buildTextOnlyHtml(text), confirmButtonText: 'Cerrar' });
}

/** Construye el HTML de evidencia solo con texto. */
export function buildTextOnlyHtml(text: string): string {
  return '<div class="text-start">' + textBlock(text) + '<p class="text-muted small">Sin archivo</p></div>';
}

/** Construye el bloque de observaciones o el vacío. */
function textBlock(text: string): string {
  if (!text) return '<p class="text-muted small mb-3">Sin texto de evidencia</p>';
  return observationBlock(escapeHtml(text));
}

/** Envuelve las observaciones escapadas en bloque claro. */
function observationBlock(safeText: string): string {
  return '<div class="mb-3"><label class="form-label small fw-bold">Observaciones</label>'
    + '<div class="border rounded p-2 bg-light" style="white-space:pre-wrap;min-height:80px;">'
    + safeText + '</div></div>';
}

/** Abre el visor de evidencia con vista previa del archivo. */
export function presentEvidenceModal(target: any, url: string, blobType: string): Promise<any> {
  return Swal.fire(evidencePreviewConfig(target, url, blobType));
}

/** Arma la configuración del visor de evidencia. */
export function evidencePreviewConfig(target: any, url: string, blobType: string): any {
  const name = fileNameOf(target.file);
  const html = buildPreviewHtml(target.comments || '', name, buildPreviewInner(url, blobType, name));
  return evidenceDialogConfig(html, url);
}

/** Arma el diálogo ancho con descarga y limpieza. */
function evidenceDialogConfig(html: string, url: string): any {
  return {
    title: 'Evidencia',
    html,
    width: '800px',
    showCancelButton: true,
    confirmButtonText: 'Descargar',
    cancelButtonText: 'Cerrar',
    didClose: () => URL.revokeObjectURL(url),
  };
}

/** Construye el HTML del visor con texto y archivo. */
export function buildPreviewHtml(text: string, fileName: string, previewInner: string): string {
  return '<div class="text-start">' + previewTextBlock(text) + previewFileLabel(fileName) + previewInner + '</div>';
}

/** Etiqueta el nombre del archivo en el visor. */
function previewFileLabel(fileName: string): string {
  return '<label class="form-label small fw-bold">Archivo: ' + escapeHtml(fileName) + '</label>';
}

/** Construye el bloque de observaciones del visor. */
function previewTextBlock(text: string): string {
  if (!text) return '';
  return observationBlock(escapeHtml(text));
}

/** Construye la vista previa o el icono de archivo. */
export function buildPreviewInner(blobUrl: string, blobType: string, fileName: string): string {
  if (isViewableType(blobType)) return frameHtml(blobUrl);
  return fileFallbackHtml(fileName);
}

/** Indica si el archivo admite vista previa embebida. */
export function isViewableType(blobType: string): boolean {
  return blobType.includes('pdf') || blobType.includes('image');
}

/** Construye el iframe de vista previa del archivo. */
function frameHtml(blobUrl: string): string {
  return '<iframe src="' + blobUrl
    + '" style="width:100%;height:420px;border:1px solid #dee2e6;border-radius:4px;" frameborder="0"></iframe>';
}

/** Construye el bloque icono cuando no hay vista previa. */
function fileFallbackHtml(fileName: string): string {
  return '<div class="text-center py-4"><i class="fas fa-file fa-3x text-muted mb-2 d-block"></i>'
    + '<p class="small text-muted">' + escapeHtml(fileName) + '</p></div>';
}

/** Muestra el error cuando la vista previa no carga. */
export function showEvidenceError(file: any): void {
  Swal.fire({ title: 'Evidencia', html: buildLoadErrorHtml(file?.downloadFile, fileNameOf(file)), confirmButtonText: 'Cerrar' });
}

/** Construye el HTML de error con enlace de descarga. */
export function buildLoadErrorHtml(downloadUrl: string, fileName: string): string {
  return '<div class="text-start"><p class="text-danger small">No se pudo cargar vista previa</p><a href="'
    + escapeHtml(downloadUrl) + '" target="_blank"><i class="fas fa-paperclip me-1"></i>'
    + escapeHtml(fileName) + '</a></div>';
}

/** Descarga el blob y libera la URL tras un segundo. */
export function downloadAndRelease(url: string, fileName: string): void {
  downloadBlobUrl(url, fileName);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Descarga el contenido apuntado por la URL. */
export function downloadBlobUrl(url: string, fileName: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
}

/** Abre el diálogo de motivo de revisión y lee el resultado. */
export function requestReviewComment(target: any): Promise<any> {
  return Swal.fire(reviewCommentConfig(target));
}

/** Arma la configuración del diálogo de motivo. */
export function reviewCommentConfig(target: any): any {
  return {
    title: 'Motivo de la revisión',
    html: reviewCommentHtml(target),
    showCancelButton: true,
    confirmButtonText: 'Guardar',
    cancelButtonText: 'Cancelar',
    focusConfirm: false,
    preConfirm: readReviewComment,
  };
}

/** Construye el HTML del motivo con el texto actual. */
function reviewCommentHtml(target: any): string {
  return '<div class="text-start"><p class="small text-muted mb-2">Explique por qué se evalúa con menor puntaje</p><textarea id="ev-review" class="form-control" rows="4">'
    + escapeHtml(target?.reviewComment || '') + '</textarea></div>';
}

/** Lee el motivo capturado en el diálogo. */
export function readReviewComment(): string | null {
  const input = document.getElementById('ev-review') as HTMLTextAreaElement;
  if (!input) return null;
  return input.value;
}
